import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  listPartnerNotifications,
  markAllPartnerNotificationsRead,
  markPartnerNotificationRead,
} from '../services/partnerNotificationsService'
import type { PartnerNotificationItem } from '../types/partnerNotification'

const POLL_MS = 45_000

export function usePartnerAdminNotifications() {
  const { user, claims } = useAuth()
  const [notifications, setNotifications] = useState<PartnerNotificationItem[]>([])
  const [loading, setLoading] = useState(false)

  const fetchNotifications = useCallback(async () => {
    if (!user || claims?.role !== 'partner') return
    setLoading(true)
    try {
      const next = await listPartnerNotifications(50)
      setNotifications(next)
    } catch {
      // Mantém lista anterior; a navbar não precisa exibir erro de rede.
    } finally {
      setLoading(false)
    }
  }, [user, claims?.role])

  useEffect(() => {
    if (!user || claims?.role !== 'partner') return
    void fetchNotifications()
    const id = window.setInterval((): void => {
      void fetchNotifications()
    }, POLL_MS)
    return () => window.clearInterval(id)
  }, [user, claims?.role, fetchNotifications])

  const unreadAdminNotifications = useMemo(() => {
    const list = notifications.filter((n) => n.readAt == null)
    list.sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return tb - ta
    })
    return list
  }, [notifications])

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await markPartnerNotificationRead(notificationId)
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, readAt: new Date().toISOString() } : n,
        ),
      )
    } catch {
      void fetchNotifications()
    }
  }, [fetchNotifications])

  const markAllAsRead = useCallback(async () => {
    try {
      await markAllPartnerNotificationsRead()
      const nowIso = new Date().toISOString()
      setNotifications((prev) => prev.map((n) => (n.readAt == null ? { ...n, readAt: nowIso } : n)))
    } catch {
      void fetchNotifications()
    }
  }, [fetchNotifications])

  return {
    unreadAdminNotifications,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
    loading,
  }
}
